import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  Activity,
  Camera,
  ChevronLeft,
  ChevronRight,
  ChevronRight as ChevronRightSm,
  Clipboard,
  Monitor,
} from 'lucide-react';
import { activityApi } from '../services/api';
import type { ActivityUsersGroupedParams, IActivityUserGroup } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import Badge, { getRoleBadgeVariant } from '../components/Badge';

const ACTION_LABELS: Record<string, { label: string; color: string }> = {
  LOGIN: { label: 'Hyrje', color: 'bg-green-100 text-green-700' },
  LOGOUT: { label: 'Dalje', color: 'bg-gray-100 text-gray-700' },
  POST_VIEW: { label: 'Shikoi postim', color: 'bg-blue-100 text-blue-700' },
  POST_CREATE: { label: 'Krijoi postim', color: 'bg-purple-100 text-purple-700' },
  POST_EDIT: { label: 'Ndryshoi postim', color: 'bg-yellow-100 text-yellow-700' },
  POST_DELETE: { label: 'Fshiu postim', color: 'bg-red-100 text-red-700' },
  SCREENSHOT_ATTEMPT: { label: 'Screenshot', color: 'bg-red-100 text-red-700' },
  APP_OPEN: { label: 'Hapi app', color: 'bg-green-100 text-green-700' },
  APP_CLOSE: { label: 'Mbylli app', color: 'bg-gray-100 text-gray-700' },
  APP_BACKGROUND: { label: 'App background', color: 'bg-gray-100 text-gray-700' },
  SCREEN_VIEW: { label: 'Shikoi ekran', color: 'bg-blue-100 text-blue-700' },
  CLIPBOARD_COPY: { label: 'Kopjoi', color: 'bg-orange-100 text-orange-700' },
  FILE_DOWNLOAD: { label: 'Shkarkoi file', color: 'bg-indigo-100 text-indigo-700' },
  NOTIFICATION_OPEN: { label: 'Hapi njoftim', color: 'bg-cyan-100 text-cyan-700' },
};

const ACTION_OPTIONS = Object.keys(ACTION_LABELS);

const POST_RELATED_ACTIONS = new Set([
  'POST_VIEW',
  'POST_CREATE',
  'POST_EDIT',
  'POST_DELETE',
  'NOTIFICATION_OPEN',
]);

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('sq-AL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function LastEventDetails({ group }: { group: IActivityUserGroup }) {
  const meta = group.lastEvent.metadata as Record<string, any> | null | undefined;
  const postId = meta?.postId as string | undefined;
  const postTitle = meta?.postTitle as string | undefined;

  if (postId && POST_RELATED_ACTIONS.has(group.lastEvent.action)) {
    return (
      <Link
        to={`/posts?postId=${postId}`}
        onClick={(e) => e.stopPropagation()}
        className="text-sm text-barrikadave-600 hover:underline truncate"
        title={postTitle || postId}
      >
        {postTitle || 'Pa titull'}
      </Link>
    );
  }

  if (!meta || Object.keys(meta).length === 0) {
    return <span className="text-sm text-gray-400">-</span>;
  }

  const text = Object.entries(meta)
    .filter(([k]) => k !== 'postId' && k !== 'postTitle')
    .map(([k, v]) => `${k}: ${v}`)
    .join(', ');
  return <span className="text-sm text-gray-500">{text || '-'}</span>;
}

export default function ActivityLogPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<ActivityUsersGroupedParams>({
    page: 1,
    limit: 30,
  });

  const { data: stats } = useQuery({
    queryKey: ['activity-stats'],
    queryFn: activityApi.getStats,
    refetchInterval: 30000,
  });

  const { data: groups, isLoading } = useQuery({
    queryKey: ['activity-users-grouped', filters],
    queryFn: () => activityApi.getUsersGrouped(filters),
    refetchInterval: 15000,
  });

  const handleFilterChange = (key: keyof ActivityUsersGroupedParams, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
      page: 1,
    }));
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Activity className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.totalToday}</p>
                <p className="text-xs text-gray-500">Veprime sot</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Monitor className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.activeUsersToday}</p>
                <p className="text-xs text-gray-500">Perdorues aktive sot</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <Camera className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.screenshotAttempts}</p>
                <p className="text-xs text-gray-500">Screenshot sot</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <Clipboard className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.clipboardCopies}</p>
                <p className="text-xs text-gray-500">Kopjime sot</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap gap-3">
          <select
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-barrikadave-500"
            value={filters.action || ''}
            onChange={(e) => handleFilterChange('action', e.target.value)}
          >
            <option value="">Te gjitha veprimet</option>
            {ACTION_OPTIONS.map((action) => (
              <option key={action} value={action}>
                {ACTION_LABELS[action].label}
              </option>
            ))}
          </select>

          <input
            type="date"
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-barrikadave-500"
            value={filters.startDate || ''}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
          />

          <input
            type="date"
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-barrikadave-500"
            value={filters.endDate || ''}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <LoadingSpinner size="lg" />
          </div>
        ) : !groups?.data?.length ? (
          <div className="text-center py-16 text-gray-400">
            <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Nuk ka aktivitete per te shfaqur</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Perdoruesi
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Veprimi i fundit
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Detajet
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Koha
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Total
                    </th>
                    <th className="px-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {groups.data.map((group) => {
                    const actionInfo = ACTION_LABELS[group.lastEvent.action] || {
                      label: group.lastEvent.action,
                      color: 'bg-gray-100 text-gray-700',
                    };
                    return (
                      <tr
                        key={group.user.id}
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/activity/${group.user.id}`)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-barrikadave-100 flex items-center justify-center shrink-0">
                              <span className="text-barrikadave-600 text-xs font-semibold">
                                {group.user.firstName[0]}
                                {group.user.lastName[0]}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {group.user.firstName} {group.user.lastName}
                              </p>
                              <Badge variant={getRoleBadgeVariant(group.user.role)}>
                                {group.user.role}
                              </Badge>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${actionInfo.color}`}
                          >
                            {actionInfo.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 max-w-[280px]">
                          <LastEventDetails group={group} />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {formatDate(group.lastEvent.createdAt)}
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-semibold text-gray-700">
                          {group.totalEvents}
                        </td>
                        <td className="px-2 text-gray-400">
                          <ChevronRightSm className="w-4 h-4" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {groups.pagination && groups.pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Faqja {groups.pagination.page} nga {groups.pagination.totalPages} ({groups.pagination.total} perdorues)
                </p>
                <div className="flex gap-2">
                  <button
                    disabled={groups.pagination.page <= 1}
                    onClick={() =>
                      setFilters((prev) => ({ ...prev, page: (prev.page || 1) - 1 }))
                    }
                    className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    disabled={groups.pagination.page >= groups.pagination.totalPages}
                    onClick={() =>
                      setFilters((prev) => ({ ...prev, page: (prev.page || 1) + 1 }))
                    }
                    className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
